import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import PageScaffold from '../components/PageScaffold';
import { authFetch } from '../utils/auth';

interface Template {
  id: number;
  templateName: string;
  language: string | null;
  formName: string | null;
  signed: boolean;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TemplateDraft {
  templateName: string;
  language: string;
  formName: string;
  signed: boolean;
  userId: string;
}

type SortColumn = 'templateName' | 'formName' | 'language' | 'signed' | 'userId' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const emptyDraft: TemplateDraft = {
  templateName: '',
  language: '',
  formName: '',
  signed: false,
  userId: '',
};

const columns: { key: SortColumn; label: string; minWidth: number }[] = [
  { key: 'templateName', label: 'Template Name', minWidth: 190 },
  { key: 'formName', label: 'Form Name', minWidth: 120 },
  { key: 'language', label: 'Language', minWidth: 120 },
  { key: 'signed', label: 'Signed', minWidth: 90 },
  { key: 'userId', label: 'User ID', minWidth: 120 },
  { key: 'createdAt', label: 'Created Date', minWidth: 150 },
];

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }
  return data as T;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function draftFromTemplate(template: Template): TemplateDraft {
  return {
    templateName: template.templateName || '',
    language: template.language || '',
    formName: template.formName || '',
    signed: template.signed,
    userId: template.userId || '',
  };
}

export default function Templates() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDraft, setUploadDraft] = useState<TemplateDraft>(emptyDraft);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState<Record<SortColumn, string>>({
    templateName: '',
    formName: '',
    language: '',
    signed: '',
    userId: '',
    createdAt: '',
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<TemplateDraft>(emptyDraft);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/templates');
      const data = await parseJsonResponse<Template[]>(response);
      setTemplates(data);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : t('templates.loadFailed', 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  const resetUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadDraft(emptyDraft);
    setPdfFile(null);
    setSaving(false);
  };

  const handleUpload = async () => {
    if (!uploadDraft.templateName.trim() || !pdfFile) {
      setError(t('templates.uploadMissingFields', 'Please enter a template name and select a PDF file.'));
      return;
    }

    const formData = new FormData();
    formData.append('templateName', uploadDraft.templateName);
    formData.append('language', uploadDraft.language);
    formData.append('formName', uploadDraft.formName);
    formData.append('signed', String(uploadDraft.signed));
    formData.append('userId', uploadDraft.userId);
    formData.append('pdfFile', pdfFile);

    setSaving(true);
    setError(null);
    try {
      const response = await authFetch('/api/templates/upload', {
        method: 'POST',
        body: formData,
      });
      await parseJsonResponse(response);
      resetUploadDialog();
      await fetchTemplates();
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : t('templates.uploadFailed', 'Failed to upload template'));
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('templates.deleteConfirm', 'Are you sure you want to delete this template?'))) return;

    setError(null);
    try {
      const response = await authFetch(`/api/templates/${id}`, { method: 'DELETE' });
      await parseJsonResponse(response);
      await fetchTemplates();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('templates.deleteFailed', 'Failed to delete template'));
    }
  };

  const startEditing = (template: Template) => {
    setEditingId(template.id);
    setEditDraft(draftFromTemplate(template));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditDraft(emptyDraft);
  };

  const saveEditing = async () => {
    if (editingId === null) return;
    if (!editDraft.templateName.trim()) {
      setError(t('templates.nameRequired', 'Template name is required.'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/templates/${editingId}/fields`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editDraft),
      });
      const data = await parseJsonResponse<{ template: Template }>(response);
      setTemplates((previous) => previous.map((template) => (
        template.id === editingId ? data.template : template
      )));
      cancelEditing();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('templates.updateFailed', 'Failed to update template'));
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const filteredTemplates = templates
    .filter((template) => {
      const matches = (value: string, filter: string) => (
        !filter || value.toLowerCase().includes(filter.toLowerCase())
      );
      return (
        matches(template.templateName || '', filters.templateName) &&
        matches(template.formName || '', filters.formName) &&
        matches(template.language || '', filters.language) &&
        matches(template.signed ? 'yes true signed' : 'no false unsigned', filters.signed) &&
        matches(template.userId || '', filters.userId) &&
        matches(formatDate(template.createdAt), filters.createdAt)
      );
    })
    .sort((a, b) => {
      const getValue = (template: Template): string | number => {
        if (sortColumn === 'createdAt') return new Date(template.createdAt).getTime();
        if (sortColumn === 'signed') return template.signed ? 1 : 0;
        return String(template[sortColumn] || '').toLowerCase();
      };
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <PageScaffold
      title={t('templates.management', 'Template Management')}
      actions={(
        <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setShowUploadDialog(true)}>
          {t('templates.addNew', 'Add New Template')}
        </Button>
      )}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog open={showUploadDialog} onClose={resetUploadDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('templates.uploadNew', 'Upload New Template')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('templates.templateName', 'Template Name')}
              value={uploadDraft.templateName}
              onChange={(event) => setUploadDraft({ ...uploadDraft, templateName: event.target.value })}
              sx={{ mt: 1 }}
              required
            />
            <TextField
              fullWidth
              label={t('templates.formName', 'Form Name')}
              value={uploadDraft.formName}
              onChange={(event) => setUploadDraft({ ...uploadDraft, formName: event.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label={t('Language', 'Language')}
              value={uploadDraft.language}
              onChange={(event) => setUploadDraft({ ...uploadDraft, language: event.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label={t('templates.userId', 'User ID')}
              value={uploadDraft.userId}
              onChange={(event) => setUploadDraft({ ...uploadDraft, userId: event.target.value })}
              sx={{ mt: 2 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={uploadDraft.signed}
                  onChange={(event) => setUploadDraft({ ...uploadDraft, signed: event.target.checked })}
                />
              )}
              label={t('templates.signed', 'Signed')}
              sx={{ mt: 1 }}
            />
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ mt: 2 }}>
              {pdfFile ? pdfFile.name : t('templates.selectPdf', 'Select PDF')}
              <input
                hidden
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => setPdfFile(event.target.files?.[0] || null)}
              />
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={resetUploadDialog} disabled={saving}>{t('Cancel', 'Cancel')}</Button>
            <Button variant="contained" onClick={() => void handleUpload()} disabled={saving}>
              {saving ? t('Uploading', 'Uploading...') : t('Upload', 'Upload')}
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilters({
              templateName: '',
              formName: '',
              language: '',
              signed: '',
              userId: '',
              createdAt: '',
            })}
          >
            {t('templates.clearFilters', 'Clear Filters')}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.key} sx={{ minWidth: column.minWidth, fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortColumn === column.key}
                        direction={sortColumn === column.key ? sortDirection : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {t(`templates.columns.${column.key}`, column.label)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ minWidth: 150, fontWeight: 600 }}>{t('common.actions', 'Actions')}</TableCell>
                </TableRow>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={`filter-${column.key}`} sx={{ py: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder={t('templates.filter', 'Filter...')}
                        value={filters[column.key]}
                        onChange={(event) => setFilters({ ...filters, [column.key]: event.target.value })}
                        fullWidth
                      />
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {t('templates.noneFound', 'No templates found')}
                    </TableCell>
                  </TableRow>
                )}
                {filteredTemplates.map((template) => (
                  <TableRow key={template.id} hover>
                    {editingId === template.id ? (
                      <>
                        <TableCell>
                          <TextField
                            size="small"
                            value={editDraft.templateName}
                            onChange={(event) => setEditDraft({ ...editDraft, templateName: event.target.value })}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={editDraft.formName}
                            onChange={(event) => setEditDraft({ ...editDraft, formName: event.target.value })}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={editDraft.language}
                            onChange={(event) => setEditDraft({ ...editDraft, language: event.target.value })}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={editDraft.signed}
                            onChange={(event) => setEditDraft({ ...editDraft, signed: event.target.checked })}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={editDraft.userId}
                            onChange={(event) => setEditDraft({ ...editDraft, userId: event.target.value })}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>{formatDate(template.createdAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={t('Save', 'Save')}>
                              <IconButton size="small" color="success" onClick={() => void saveEditing()} disabled={saving}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('Cancel', 'Cancel')}>
                              <IconButton size="small" onClick={cancelEditing} disabled={saving}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{template.templateName || t('N/A', 'N/A')}</TableCell>
                        <TableCell>{template.formName || t('N/A', 'N/A')}</TableCell>
                        <TableCell>{template.language || t('N/A', 'N/A')}</TableCell>
                        <TableCell>{template.signed ? t('Yes', 'Yes') : t('No', 'No')}</TableCell>
                        <TableCell>{template.userId || t('N/A', 'N/A')}</TableCell>
                        <TableCell>{formatDate(template.createdAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={t('Edit', 'Edit')}>
                              <IconButton size="small" color="warning" onClick={() => startEditing(template)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('View', 'View')}>
                              <IconButton size="small" color="info" onClick={() => navigate(`/templates/${template.id}`)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('Delete', 'Delete')}>
                              <IconButton size="small" color="error" onClick={() => void handleDelete(template.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
    </PageScaffold>
  );
}
